import _                                    from 'underscore';
import { Moment }                           from 'moment';
import React, { FC, ReactNode, useContext } from 'react';
import { Segment }                          from 'semantic-ui-react';

import { CorvalAuditEntry, CorvalProjectSubObjective, T_OR_NULL, Uuid } from '../../../../../types';

import { DecisionEngineQueries, SystemQueries } from '../../../../../queries';
import { TimeService }                          from '../../../../../services';
import { withoutType }                          from '../../../../../store/_apolloUtil';
import { AUDIT_RECORD_TYPE }                    from '../../../../../constants/SystemRecordEnums';
import { ItemFromQuery }                        from '../../../../components/from-query/ItemFromQuery';
import { HumanizedDateRangeLabel }              from '../../../../components/labels/HumanizedDateRangeLabel';
import { AuditMemoList }                        from '../../../../components/audits/AuditMemoList';
import { ProjectContext }                       from '../../ProjectIndexPage';
import { InlineLoadingIndicatorLabel }          from '../../../../components/util/loading/InlineLoadingIndicatorLabel';
import { maybeStaticVersionField }              from "@src/util";


interface Props {
    id: Uuid
}

const defaultProps: Props = {
    id: null,
}

interface ObjectiveDetailProjectEstimatedTimingChildProps {
    dateRangeComponent: ReactNode,
    humanizedDateRangeComponent: ReactNode,
    auditComponent: ReactNode,
    startTime: Moment,
    endTime: Moment,
    originalStartTime: Moment | null,
    auditRecords: CorvalAuditEntry[],
}

const ObjectiveDetailProjectEstimatedTiming: FC<Props> = ( props ) => {

    let { projectId, isCorvalManaged, projectVersionId } = useContext( ProjectContext );

    return (

        <>
            <ItemFromQuery
                as={ React.Fragment }
                query={ DecisionEngineQueries.queries.GET_PROJECT_SUBOBJECTIVES( `
					id
					startTimeUtc
					endTimeUtc
					startTimeCalculatedUtc
					${ SystemQueries.fragments.AUDIT_RECORDS.QUERY_RETURN }
				`, projectVersionId ) }
                queryOptions={ {
                    variables: {
                        projectId,
                        projectVersionId,
                        where: { subObjectiveId: { eq: props.id } }
                    }
                } }
                loadingComponent={ () => <InlineLoadingIndicatorLabel/> }
                queryResultObject={ ( data ) => {

                    const projectSubObjectivesField = maybeStaticVersionField( 'projectSubObjectives', !isCorvalManaged );

                    if( data[ projectSubObjectivesField ].length ){

                        let projectSubObjective = _.first( data[ projectSubObjectivesField ] );

                        return {
                            ...withoutType( projectSubObjective ),
                            startTimeUtc: TimeService.ISOtoMoment( projectSubObjective.startTimeUtc ),
                            endTimeUtc:   TimeService.ISOtoMoment( projectSubObjective.endTimeUtc ),
                        }
                    }

                    return null;
                } }>

                { ( objective: T_OR_NULL<CorvalProjectSubObjective> ) => {

                    let auditRecords = _.filter( objective.auditEntries, { auditType: AUDIT_RECORD_TYPE.PROJECT_OBJECTIVE_TIMING_OVERRIDE } ) ?? [];

                    let dateRangeComponent = <HumanizedDateRangeLabel withSummaryLabel={ false } startDate={ objective.startTimeUtc } endDate={ objective.endTimeUtc }/>;
                    let humanizedDateRangeComponent = <HumanizedDateRangeLabel withRangeLabels={ false } startDate={ objective.startTimeUtc } endDate={ objective.endTimeUtc }/>;
                    let auditComponent = !_.isEmpty( auditRecords ) ? (
                        <Segment secondary>
                            <AuditMemoList
                                timeBoundary={ 'newest' }
                                records={ objective.auditEntries }
                                types={ [ AUDIT_RECORD_TYPE.PROJECT_OBJECTIVE_TIMING_OVERRIDE ] }
                                actionVerb={ 'reschedule' }
                                auditSubject={ 'this objective' }
                                auditAdditionalInfo={ `The original starting time was ${ TimeService.ISOtoDisplayDate( objective.startTimeCalculatedUtc ) }.` }
                            />
                        </Segment>
                    ) : null;

                    let childRenderProps: ObjectiveDetailProjectEstimatedTimingChildProps = {
                        dateRangeComponent,
                        humanizedDateRangeComponent,
                        auditComponent,
                        startTime:         TimeService.ISOtoMoment( objective.startTimeUtc, true ),
                        endTime:           TimeService.ISOtoMoment( objective.endTimeUtc, true ),
                        originalStartTime: auditRecords ? TimeService.ISOtoMoment( objective.startTimeCalculatedUtc, true ) : null,
                        auditRecords
                    };

                    return (
                        <>
                            { _.isFunction( props.children ) ? props.children( childRenderProps ) : null }
                        </>
                    )
                } }

            </ItemFromQuery>

        </>
    );
};

ObjectiveDetailProjectEstimatedTiming.defaultProps = defaultProps;

export {
    ObjectiveDetailProjectEstimatedTiming,
    ObjectiveDetailProjectEstimatedTimingChildProps
}
